import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { catchError, EMPTY, filter, map, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { ActionAddOrRemoveOwnerComponent } from 'src/app/components/actions/action-add-or-remove-owner/action-add-or-remove-owner.component';
import { ActionDeleteComponent } from 'src/app/components/actions/action-delete/action-delete.component';
import {
  ActionSendEmailComponent,
  EmailData,
} from 'src/app/components/actions/action-send-email/action-send-email.component';
import { ActionSetAccessComponent } from 'src/app/components/actions/action-set-access/action-set-access.component';
import { ActionSetResourceQuarantineStateComponent } from 'src/app/components/actions/action-set-resource-quarantine-state/action-set-resource-quarantine-state.component';
import { GetFlowRunsActionComponent } from 'src/app/components/actions/get-flow-runs-action/get-flow-runs-action.component';
import {
  ActionSetCopilotAuthenticationDialogComponent,
  SetCopilotAuthenticationDialogResult,
} from 'src/app/components/actions/powerplatform-actions/set-copilot-authentication/action-set-copilot-authentication-dialog/action-set-copilot-authentication-dialog.component';
import {
  ConfirmationDialogComponent,
  ConfirmData,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { GetResourceDefinitionActionComponent } from 'src/app/components/get-resource-definition-action/get-resource-definition-action.component';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { Label } from 'src/app/labels/englishLabels';
import { Platform, ResourceType } from 'src/app/models/api';
import { DownloadAttachmentActionOutputs } from 'src/app/models/api/actions/downloadAttachment.model';
import { getIntegrationStatusLabel } from 'src/app/models/api/integration.model';
import {
  Attachment,
  AttachmentType,
  InventoryEntityField,
  InventoryResourceKnownRelation,
  ResourceStatus,
} from 'src/app/models/api/inventoryResource.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import {
  ActionDeleteResponseModel,
  ActionResultCode,
  ActionsHttpServiceService,
  AddOrRemoveOwnerParamsModel,
  AutomationActivationActions,
  AutomationActivationParams,
  PowerplatformAgentActionAuthenticationMode,
  PowerplatformAgentActionConfirmationMode,
  RequiredParamsForActionsHttpRequests,
  RequiredParamsForChatGptEnterpriseDeleteConversationAction,
  RequiredParamsForViolationActionsHttpRequests,
  ResourceActionOutput,
  SetApplicationQuarantineStateParamsModel,
} from '../../httpServices/actionsHttpService/actions-http-service.service';
import { HttpErrorHandlerService } from '../../httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from '../../infra/notifications/notifications.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { StoreService } from '../../infra/store/store.service';
import { ActionWithConfirmationTextComponent } from '../../../components/actions/action-with-confirmation-text/action-with-confirmation-text.component';
import { ActionDownloadComponent } from '../../../components/actions/action-download/action-download.component';

export interface EmailPlaceholder {
  label: string;
  description: string;
}

export interface OptionalParamsForActions {
  platformUrl?: string | null;
}

export interface GetDefinitionEnabledResponse {
  enabled: boolean;
  disabledReason?: string;
}

export interface ParamsForActions<requiredParamsType extends RequiredParamsForActionsHttpRequests> {
  requiredParams: requiredParamsType;
  optionalParams: OptionalParamsForActions;
}

/**
 * Dialog action result type for standardized dialog handling
 */
export interface DialogActionResult<T = unknown> {
  success: boolean;
  data?: T;
}

@Injectable({
  providedIn: 'root',
})
export class CommonActionsService {
  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _permissionsService: PermissionsService,
    private readonly _actionsHttpService: ActionsHttpServiceService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _storeService: StoreService
  ) {}

  public get supportedSetAccessResourceTypesArray(): ResourceType[] {
    return [ResourceType.flow, ResourceType.canvas_app];
  }

  public get supportedAddOwnersResourceTypesArray(): ResourceType[] {
    return [ResourceType.environment, ResourceType.custom_connector];
  }

  public get supportedRemoveOwnersResourceTypesArray(): ResourceType[] {
    return [ResourceType.environment];
  }

  public get supportedDeletionResourceTypesArray(): ResourceType[] {
    return [ResourceType.environment, ResourceType.flow, ResourceType.canvas_app, ResourceType.connection];
  }

  public get supportedGetResourceDefinitionTypesArray(): ResourceType[] {
    return [ResourceType.flow];
  }

  /**
   * Opens a dialog and returns an Observable that emits the result
   * @param component The dialog component to open
   * @param config The dialog configuration
   * @returns Observable<R> that emits the dialog result
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _openDialog<T, R = boolean>(component: any, config: MatDialogConfig<T>): Observable<R> {
    return new Observable<R>((observer) => {
      const dialogRef = this._matDialog.open(component, config);

      const subscription = dialogRef.afterClosed().subscribe({
        next: (result: R) => {
          observer.next(result);
          observer.complete();
        },
        error: (err) => observer.error(err),
      });

      return () => {
        subscription.unsubscribe();
        if (dialogRef.getState() === 0) {
          // MatDialogState.OPEN = 0
          dialogRef.close();
        }
      };
    });
  }

  /**
   * Opens a confirmation dialog with standardized configuration
   * @param title Dialog title
   * @param content Dialog content
   * @param positiveText Positive button text (default: "Confirm")
   * @param negativeText Negative button text (default: "Cancel")
   * @returns Observable<boolean> that emits true if confirmed
   */
  private _openConfirmationDialog(
    title: string,
    content: string,
    positiveText: string = Label.get('common.confirm'),
    negativeText: string = Label.get('common.cancel')
  ): Observable<boolean> {
    const confirmData: ConfirmData = {
      title,
      content,
      positiveOptionText: positiveText,
      negativeOptionText: negativeText,
    };

    return this._openDialog<ConfirmData, boolean>(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    });
  }

  /**
   * Handles HTTP errors with a standardized approach
   * @param error HTTP error response
   * @param errorPrefix Prefix for the error message
   * @param defaultReason Default reason if no detail is available
   */
  private _handleHttpError(error: HttpErrorResponse, errorPrefix: string, defaultReason: string): Observable<never> {
    if (this._httpErrorHandlerService.isUnhandledExceptionError(error.status)) {
      this._httpErrorHandlerService.handle(error, true);
    } else {
      let reason = defaultReason;
      if (typeof error.error?.detail === 'string') {
        reason = error.error.detail;
      }
      this._notificationService.error(`${errorPrefix}, reason: ${reason}`);
    }
    return EMPTY;
  }

  /**
   * Generic method to handle an API action with confirmation dialog
   * @param confirmTitle Confirmation dialog title
   * @param confirmContent Confirmation dialog content
   * @param apiCall API call function that returns Observable
   * @param successMessage Success message to display
   * @param errorPrefix Error prefix for error message
   * @returns ReplaySubject<boolean> that emits true if action succeeded
   */
  private _handleConfirmAction<T>(
    confirmTitle: string,
    confirmContent: string,
    apiCall: () => Observable<T>,
    successMessage: string,
    errorPrefix: string
  ): ReplaySubject<boolean> {
    const result = new ReplaySubject<boolean>(1);

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() =>
          apiCall().pipe(
            map(() => {
              this._notificationService.success(successMessage);
              return true;
            }),
            catchError((error: HttpErrorResponse) => {
              this._handleHttpError(error, errorPrefix, "Error without 'detail' field");
              return of(false);
            })
          )
        ),
        // If confirmation was denied (filter removed it), return false
        catchError(() => of(false))
      )
      .subscribe(result);

    return result;
  }

  public showRawData(actionsHttpParams: RequiredParamsForActionsHttpRequests): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const confirmTitle = Label.get('commonActions.getResourceDefinitionAction.sensitiveDataDisclaimerPopupTitle');
    const confirmContent = Label.get('commonActions.getResourceDefinitionAction.sensitiveDataDisclaimer');

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }

          return this._openDialog(GetResourceDefinitionActionComponent, {
            disableClose: false,
            panelClass: 'sidepanel-dialog-container',
            width: '700px',
            autoFocus: false,
            data: actionsHttpParams,
          }).pipe(map((actionSucceeded) => !!actionSucceeded));
        })
      )
      .subscribe(success);

    return success;
  }

  public OpenShowFlowRunsConfirmationDialogDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const confirmTitle = Label.get('commonActions.getFlowRunsAction.confirmationPopupTitle');
    const confirmContent = Label.get('commonActions.getFlowRunsAction.confirmationPopupContent');

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }

          return this._openDialog(GetFlowRunsActionComponent, {
            disableClose: false,
            panelClass: 'sidepanel-dialog-container',
            width: '700px',
            autoFocus: false,
            data: actionsHttpParams,
          }).pipe(map((actionSucceeded) => !!actionSucceeded));
        })
      )
      .subscribe(success);

    return success;
  }

  public addOrRemoveOwnerAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    isAddOwner: boolean,
    optionalParams: OptionalParamsForActions = {}
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    actionsHttpParams.isAdminEndpoint =
      this.getPermissionRequiredForResourceAction(actionsHttpParams.resourceType) == PermissionEnum.admin;
    const actionRequiredParams: AddOrRemoveOwnerParamsModel = {
      isAddOwner: isAddOwner,
      ...actionsHttpParams,
    };

    this._openDialog(ActionAddOrRemoveOwnerComponent, {
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
      width: '350px',
      autoFocus: false,
      data: {
        requiredParams: actionRequiredParams,
        optionalParams: optionalParams,
      } as ParamsForActions<AddOrRemoveOwnerParamsModel>,
    }).subscribe(success);

    return success;
  }

  public setAccessAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    optionalParams: OptionalParamsForActions = {}
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionSetAccessComponent, {
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
      width: '500px',
      autoFocus: false,
      data: {
        requiredParams: actionsHttpParams,
        optionalParams: optionalParams,
      } as ParamsForActions<RequiredParamsForActionsHttpRequests>,
    }).subscribe(success);

    return success;
  }

  public getResourceAttachment(
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    attachmentId: string
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._actionsHttpService
      .downloadAttachment(actionsHttpParams.integrationId, actionsHttpParams.resourceId, attachmentId)
      .pipe(
        map((response: DownloadAttachmentActionOutputs) => {
          window.open(response.downloadUrl);
          return true;
        }),
        catchError((error) => {
          this._httpErrorHandlerService.handle(error);
          return of(false);
        })
      )
      .subscribe(success);

    return success;
  }

  public getResourceMakerEmails(
    ownedBy: InventoryEntityField<InventoryResourceKnownRelation[]> | undefined,
    createdBy: InventoryEntityField<InventoryResourceKnownRelation> | undefined
  ): Set<string> {
    let ownerEmails: string[];
    if (ownedBy?.value) {
      ownerEmails = ownedBy.value.map((owner) => owner.email?.value).filter((email): email is string => email !== null);
    } else {
      ownerEmails = [];
    }
    const creatorEmail = createdBy?.value?.email?.value;
    if (ownerEmails.length > 0 && ownerEmails.every((email) => email !== null)) {
      return new Set(ownerEmails);
    }
    if (creatorEmail) {
      return new Set([creatorEmail]);
    }
    return new Set();
  }

  public powerplatformSetCopilotAuthenticationAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    // Create an error handler specific to this operation
    const handleSetAuthError = (error: HttpErrorResponse): Observable<boolean> => {
      if (error.status === 422 && error.error && error.error.title == 'Platform API invalid input') {
        this._notificationService.info(
          "Enforcing 'user sign-in' will work only if Copilot is already using manual authentication"
        );
      } else if (error.status === 422) {
        this._notificationService.error('Failed to set authentication. ' + (error.error?.detail || ''));
      } else {
        this._httpErrorHandlerService.handle(error);
      }
      return of(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._openDialog<any, SetCopilotAuthenticationDialogResult>(ActionSetCopilotAuthenticationDialogComponent, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        integrationId: actionsHttpParams.integrationId,
        resourceId: actionsHttpParams.resourceId,
      },
    })
      .pipe(
        switchMap((result) => {
          if (!result || result.type === null) {
            return of(false);
          }

          const type = result.type;
          let action = 'save';
          if (actionsHttpParams.resourceStatus && actionsHttpParams.resourceStatus === ResourceStatus.active) {
            action = 'saveAndPublish';
          }

          const successfullyText = 'Copilot authentication updated successfully';

          if (action === 'save') {
            return this._actionsHttpService
              .powerplatformSetCopilotAuthentication(
                actionsHttpParams.integrationId,
                actionsHttpParams.resourceId,
                type
              )
              .pipe(
                map(() => {
                  this._notificationService.success(successfullyText);
                  return true;
                }),
                catchError(handleSetAuthError)
              );
          } else {
            // saveAndPublish
            return this._actionsHttpService
              .powerplatformSetCopilotAuthentication(
                actionsHttpParams.integrationId,
                actionsHttpParams.resourceId,
                type
              )
              .pipe(
                switchMap(() =>
                  this._actionsHttpService
                    .powerplatformPublishCopilot(actionsHttpParams.integrationId, actionsHttpParams.resourceId)
                    .pipe(
                      map(() => {
                        this._notificationService.success(successfullyText);
                        return true;
                      }),
                      catchError(() => {
                        this._notificationService.error(
                          'Copilot authentication saved successfully but failed to publish'
                        );
                        return of(false);
                      })
                    )
                ),
                catchError(handleSetAuthError)
              );
          }
        })
      )
      .subscribe(success);

    return success;
  }

  public deleteAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionDeleteComponent, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        itemName: `${this._displayCamelCaseStringPipe.transform(actionsHttpParams.resourceType)} - ${resourceName}`,
      },
    })
      .pipe(
        switchMap((toDelete) => {
          if (!toDelete) {
            return of(false);
          }

          return this._actionsHttpService.deleteResource(actionsHttpParams).pipe(
            map((response: ActionDeleteResponseModel) => {
              if (response.alreadyDeleted) {
                this._notificationService.error('Resource was already deleted');
                return false;
              } else {
                this._notificationService.success('Resource was deleted successfully');
                return true;
              }
            }),
            catchError((error: HttpErrorResponse) => {
              if (this._httpErrorHandlerService.isUnhandledExceptionError(error.status)) {
                this._httpErrorHandlerService.handle(error);
              } else {
                let text = "Error without 'detail' field";
                if (typeof error.error?.detail === 'string') {
                  text = error.error.detail;
                }
                this._notificationService.error(`Resource could not be deleted, reason: ${text}`);
              }
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public sendEmailAction(emailData: EmailData): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionSendEmailComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
      width: '800px',
      height: '605px',
      autoFocus: false,
      data: emailData,
    }).subscribe(success);

    return success;
  }

  public secureFlowSteps(params: RequiredParamsForViolationActionsHttpRequests): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const confirmTitle = Label.get('commonActions.manageSecureFlowStepsSolutionAwareAction.title');
    const confirmContent = Label.get('commonActions.manageSecureFlowStepsSolutionAwareAction.content');

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }

          const combineNotificationText = (title: string, description: string | null) =>
            description ? `${title}: ${description}` : title;

          return this._actionsHttpService.secureFlowSteps(params).pipe(
            map((response: ResourceActionOutput) => {
              if (response.resultCode === ActionResultCode.failure) {
                this._notificationService.error(combineNotificationText(response.title, response.description));
                return false;
              } else if (response.resultCode === ActionResultCode.no_effect) {
                this._notificationService.info(combineNotificationText(response.title, response.description));
                return true;
              } else {
                this._notificationService.success(combineNotificationText(response.title, response.description));
                return true;
              }
            }),
            catchError((error: HttpErrorResponse) => {
              this._handleHttpError(error, 'Action execution failed', 'Error in Powerplatform API');
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public powerplatformAgentActionEnableAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.enableCopilotAction.title');
    const confirmContent = Label.get('commonActions.enableCopilotAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionEnableAction(actionsHttpParams),
      'Resource was enabled successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionDisableAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.disableCopilotAction.title');
    const confirmContent = Label.get('commonActions.disableCopilotAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionDisableAction(actionsHttpParams),
      'Resource was disabled successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionSetMakerAuthenticationMode(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.setMakerAuthenticationMode.title');
    const confirmContent = Label.get('commonActions.setMakerAuthenticationMode.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () =>
        this._actionsHttpService.agentActionUpdateAuthenticationMode(
          actionsHttpParams,
          PowerplatformAgentActionAuthenticationMode.maker
        ),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionSetInvokerAuthenticationMode(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.setInvokerAuthenticationMode.title');
    const confirmContent = Label.get('commonActions.setInvokerAuthenticationMode.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () =>
        this._actionsHttpService.agentActionUpdateAuthenticationMode(
          actionsHttpParams,
          PowerplatformAgentActionAuthenticationMode.invoker
        ),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionSetStrictConfirmationMode(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.setStrictConfirmationMode.title');
    const confirmContent = Label.get('commonActions.setStrictConfirmationMode.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () =>
        this._actionsHttpService.agentActionUpdateConfirmationMode(
          actionsHttpParams,
          PowerplatformAgentActionConfirmationMode.strict
        ),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionSetDisabledConfirmationMode(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.setDisabledConfirmationMode.title');
    const confirmContent = Label.get('commonActions.setDisabledConfirmationMode.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () =>
        this._actionsHttpService.agentActionUpdateConfirmationMode(
          actionsHttpParams,
          PowerplatformAgentActionConfirmationMode.disabled
        ),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentEnableWebBrowsingAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.enableWebBrowsingAction.title');
    const confirmContent = Label.get('commonActions.enableWebBrowsingAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionEnableWebBrowsing(actionsHttpParams),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentDisableWebBrowsingAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.disableWebBrowsingAction.title');
    const confirmContent = Label.get('commonActions.disableWebBrowsingAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionDisableWebBrowsing(actionsHttpParams),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionDeleteAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `Agent Action - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.agentActionDeleteAction(actionsHttpParams),
      title
    );
  }

  public powerplatformAgentDeleteAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `Agent - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.agentActionDeleteAction(actionsHttpParams),
      title
    );
  }

  private _deleteResourceWithConfirmation(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deleteAction: () => Observable<any>,
    title: string | undefined = undefined
  ): ReplaySubject<boolean> {
    return this._actionWithConfirmation(
      resourceName,
      'Delete',
      'Could not delete resource',
      'Resource was deleted successfully',
      'Permanent delete',
      actionsHttpParams,
      ActionDeleteComponent,
      () => deleteAction(),
      title
    );
  }

  /**
   * Common method for deletion actions that use the ActionDeleteComponent
   */
  private _actionWithConfirmation<TAction = ActionWithConfirmationTextComponent>(
    resourceName: string,
    actionName: string,
    errorMessage: string,
    successMessage: string,
    confirmationText: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    actionType: TAction,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: () => Observable<any>,
    title: string | undefined = undefined
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);
    const titleToShow =
      title || `${this._displayCamelCaseStringPipe.transform(actionsHttpParams.resourceType)} - ${resourceName}`;
    this._openDialog(actionType, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        itemName: titleToShow,
        confirmationText: confirmationText,
      },
    })
      .pipe(
        switchMap((toDelete) => {
          if (!toDelete) {
            return of(false);
          }

          return action().pipe(
            map(() => {
              this._notificationService.success(successMessage);
              return true;
            }),
            catchError((error: HttpErrorResponse) => {
              if (this._httpErrorHandlerService.isUnhandledExceptionError(error.status)) {
                this._httpErrorHandlerService.handle(error);
              } else {
                let text = "Error without 'detail' field";
                if (typeof error.error?.detail === 'string') {
                  text = error.error.detail;
                }
                // Handle specific error for resource not found = PLATFORM_API_FAILURE_RESOURCE_NOT_FOUND
                // Currently this is a "magic string" because our internal API does not return the errorCode as an int but as free text (string)
                if (error.error.errorCode === 'PLATFORM_API_FAILURE_RESOURCE_NOT_FOUND') {
                  this._notificationService.error(
                    `Error: The resource you are trying to ${actionName.toLowerCase()} no longer exists.`
                  );
                  return of(false);
                } else {
                  this._notificationService.error(`${errorMessage}, reason: ${text}`);
                }
              }
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public powerplatformAgentActionEnforceUserAuthenticationAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.enforceUserAuthenticationAction.title');
    const confirmContent = Label.get('commonActions.enforceUserAuthenticationAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionEnforceUserAuthenticationAction(actionsHttpParams),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionEnforceUserConfirmationAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.enforceUserConfirmationAction.title');
    const confirmContent = Label.get('commonActions.enforceUserConfirmationAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionEnforceUserConfirmationAction(actionsHttpParams),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public chatGptEnterpriseDeleteGptAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `GPT - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptDeleteAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseDeleteUserCanvasAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `GPT - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptUserCanvasDeleteAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseGetUserCanvasAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `GPT - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptGetUserCanvasAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseIsolateGptAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.isolateGptAction.title');
    const confirmContent = Label.get('commonActions.isolateGptAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.gptIsolateAction(actionsHttpParams),
      'Resource was isolated successfully',
      'Resource could not be isolated'
    );
  }

  public chatGptEnterpriseDeleteGptKnowledgeAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `${resourceName} from the GPT workspace`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptDeleteGptKnowledgeAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseGetGptKnowledgeFileUrlAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    return this._actionWithConfirmation(
      resourceName,
      'Download',
      Label.get('commonActions.download.errorMessage'),
      Label.get('commonActions.download.successMessage'),
      'Confirm download',
      actionsHttpParams,
      ActionDownloadComponent,
      () => this._actionsHttpService.gptGetKnowledgeFileUrlAction(actionsHttpParams)
    );
  }

  public chatGptEnterpriseDeleteGptConversationAction(
    actionsHttpParams: RequiredParamsForChatGptEnterpriseDeleteConversationAction
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.deleteGptConversationAction.title');
    const confirmContent = Label.get('commonActions.deleteGptConversationAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.gptDeleteConversationAction(actionsHttpParams),
      'Conversation was deleted successfully',
      'Conversation could not be deleted'
    );
  }

  public setResourceQuarantineStateAction(
    resourceName: string,
    actionsHttpParams: SetApplicationQuarantineStateParamsModel
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionSetResourceQuarantineStateComponent, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        itemName: resourceName,
        quarantineStatus: actionsHttpParams.quarantine_status,
      },
    })
      .pipe(
        switchMap((shouldSetQuarantineState) => {
          if (!shouldSetQuarantineState) {
            return of(false);
          }

          return this._actionsHttpService.setResourceQuarantineState(actionsHttpParams).pipe(
            map(() => {
              this._notificationService.success(`Resource was ${actionsHttpParams.quarantine_status} successfully`);
              return true;
            }),
            catchError((error: HttpErrorResponse) => {
              this._handleHttpError(
                error,
                `Resource could not be ${actionsHttpParams.quarantine_status}`,
                "Error without 'detail' field"
              );
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public automationActivation(params: AutomationActivationParams, resourceType: ResourceType): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);
    const resourceTypeStr = resourceType == ResourceType.flow ? 'Flow' : 'Recipe';
    const action = params.action == AutomationActivationActions.start ? 'started' : 'stopped';

    this._actionsHttpService
      .automationActivation(params, resourceType)
      .pipe(
        map(() => {
          this._notificationService.success(`${resourceTypeStr} was ${action} successfully`);
          return true;
        }),
        catchError((error: HttpErrorResponse) => {
          this._handleHttpError(error, `${resourceTypeStr} could not be ${action}`, 'Error not from Zenity API');
          return of(false);
        })
      )
      .subscribe(success);

    return success;
  }

  public getPermissionRequiredForResourceAction(resourceType: ResourceType): PermissionEnum {
    switch (resourceType) {
      case ResourceType.environment:
        return PermissionEnum.admin;
      case ResourceType.flow:
      case ResourceType.canvas_app:
      case ResourceType.custom_connector:
      case ResourceType.connection:
        return PermissionEnum.operator;
      case ResourceType.recipe:
        return PermissionEnum.operator;
      default:
        throw new Error(
          `CommonActionsService getPermissionRequiredForResourceAction: Resource type ${resourceType} is not supported`
        );
    }
  }

  public isGetDefinitionOptionVisible(platform: Platform, resourceType: ResourceType): boolean {
    if (!this._permissionsService.hasPermissions(PermissionEnum.operator)) {
      return false;
    }
    return platform === Platform.powerplatform && this.supportedGetResourceDefinitionTypesArray.includes(resourceType);
  }

  public isGetDefinitionOptionEnabled(integrationId: string): Observable<GetDefinitionEnabledResponse> {
    return this._storeService.integrations$.pipe(
      switchMap((integrations) => {
        for (const integration of integrations) {
          if (integration.id === integrationId) {
            if (getIntegrationStatusLabel(integration.connectionStatus) !== StatusLabelEnum.active) {
              return of({
                enabled: false,
                disabledReason: Label.get('commonActions.getResourceDefinitionAction.inactiveIntegrationMsg'),
              });
            } else {
              return of({
                enabled: true,
                disabledReason: undefined,
              });
            }
          }
        }

        console.error('Could not find integration to determine if get definition action is enabled.');
        return of({ enabled: false, disabledReason: undefined });
      })
    );
  }

  public findSbomAttachment(attachments: Attachment[]): Attachment | undefined {
    if (!attachments) {
      return undefined;
    }
    return attachments.find((attachment) => attachment.type === AttachmentType.sbom);
  }
}
